<template>
  <div class="px-4 py-5 sm:px-6 lg:px-8">
    <div class="mx-auto max-w-7xl">
      <div class="mb-6">
        <p class="text-xs font-semibold uppercase text-bochas-rojo">
          Imágenes del sitio
        </p>
        <h2 class="mt-1 text-xl font-bold text-white sm:text-2xl">
          Portadas
        </h2>
        <p class="mt-2 text-sm text-bochas-text-muted">
          Administrá las imágenes principales del inicio y de cada página.
        </p>
      </div>

      <div v-if="loading" class="card-fbochas p-6 text-bochas-text-muted">
        Cargando portadas...
      </div>

      <div v-else class="space-y-4">
        <div class="section-heading">
          <span class="section-heading__number">1</span>
          <div>
            <h3 class="text-base font-bold text-white">Portada principal</h3>
            <p class="mt-0.5 text-xs text-bochas-text-muted">Carrusel principal del inicio.</p>
          </div>
        </div>
        <article class="card-fbochas overflow-hidden p-0">
          <div class="space-y-4 p-4">
            <div>
              <h3 class="text-base font-bold text-white">
                {{ homeHero.label }}
              </h3>
              <p class="mt-1 text-xs text-bochas-text-muted">
                Imagen, título y subtítulo principal del home.
              </p>
            </div>

            <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div
                v-for="(slide, index) in homeHeroSlides"
                :key="slide.key"
                class="space-y-2 rounded-lg border border-white/10 bg-white/[0.03] p-2.5"
              >
                <div class="aspect-[5/3] overflow-hidden rounded-md bg-black/30">
                  <img
                    v-if="slide.imageUrl"
                    :src="slide.imageUrl"
                    :alt="slide.alt || slide.label"
                    class="h-full w-full object-cover"
                  />
                  <div
                    v-else
                    class="flex h-full items-center justify-center text-sm text-bochas-text-muted"
                  >
                    Sin imagen
                  </div>
                </div>

                <input
                  v-model="slide.alt"
                  type="text"
                  class="admin-input"
                  :placeholder="index === 0 ? 'Texto alternativo' : `Texto alternativo ${index + 1}`"
                  @change="saveAlt(slide)"
                />

                <input
                  v-model="slide.title"
                  type="text"
                  class="admin-input"
                  :placeholder="index === 0 ? 'Título' : `Título ${index + 1}`"
                />

                <textarea
                  v-model="slide.subtitle"
                  rows="2"
                  class="admin-input"
                  :placeholder="index === 0 ? 'Subtítulo' : `Subtítulo ${index + 1}`"
                ></textarea>

                <input
                  :ref="(el) => setInputRef(slide.key, el)"
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="onFileChange(slide, $event)"
                />

                <button
                  type="button"
                  class="btn-bochas-rojo w-full px-3 py-2 text-xs"
                  :disabled="uploadingKey === slide.key"
                  @click="inputRefs[slide.key]?.click()"
                >
                  {{
                    uploadingKey === slide.key
                      ? "Subiendo..."
                      : slide.imageUrl
                        ? `Cambiar imagen ${index + 1}`
                        : `Subir imagen ${index + 1}`
                  }}
                </button>

                <button
                  type="button"
                  class="w-full rounded-lg border border-white/10 bg-white/10 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/15"
                  :disabled="savingKey === slide.key"
                  @click="saveContent(slide)"
                >
                  {{
                    savingKey === slide.key
                      ? "Guardando..."
                      : `Guardar info ${index + 1}`
                  }}
                </button>
              </div>
            </div>

            <button
              type="button"
              class="rounded-lg border border-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
              @click="addHomeHeroSlide"
            >
              Agregar otra imagen y título
            </button>

            <p v-if="errors.home" class="text-xs text-bochas-rojo">
              {{ errors.home }}
            </p>
          </div>
        </article>

        <section
          v-for="(group, groupIndex) in mediaGroups"
          :key="group.key"
          class="pt-4"
        >
          <div class="section-heading mb-4">
            <span class="section-heading__number">{{ groupIndex + 2 }}</span>
            <div>
              <h3 class="text-base font-bold text-white">{{ group.label }}</h3>
              <p class="mt-0.5 text-xs text-bochas-text-muted">{{ group.description }}</p>
            </div>
            <span class="ml-auto text-xs font-semibold text-bochas-text-muted">
              {{ group.items.length }} imágenes
            </span>
          </div>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <article
              v-for="item in group.items"
              :key="item.key"
              class="card-fbochas overflow-hidden p-0"
            >
            <div class="aspect-video bg-black/30">
              <img
                v-if="item.imageUrl"
                :src="item.imageUrl"
                :alt="item.alt || item.label"
                class="h-full w-full object-cover"
              />
              <div
                v-else
                class="flex h-full items-center justify-center text-sm text-bochas-text-muted"
              >
                Sin imagen
              </div>
            </div>

            <div class="space-y-3 p-4">
              <div>
                <h3 class="text-base font-bold text-white">{{ item.label }}</h3>
                <p class="mt-1 text-xs text-bochas-text-muted">{{ item.key }}</p>
              </div>

              <div class="space-y-3 rounded-lg border border-white/10 bg-white/[0.03] p-3">
                <p class="text-xs font-semibold uppercase text-bochas-text-muted">
                  Imagen
                </p>
                <input
                  v-if="!item.key.startsWith('alojamiento.')"
                  v-model="item.alt"
                  type="text"
                  class="admin-input"
                  placeholder="Texto alternativo"
                  @change="saveAlt(item)"
                />
                <input
                  :ref="(el) => setInputRef(item.key, el)"
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="onFileChange(item, $event)"
                />
                <button
                  type="button"
                  class="btn-bochas-rojo w-full px-4 py-2 text-sm"
                  :disabled="uploadingKey === item.key"
                  @click="inputRefs[item.key]?.click()"
                >
                  {{
                    uploadingKey === item.key
                      ? "Subiendo..."
                      : item.imageUrl
                        ? item.key.startsWith("alojamiento.")
                          ? "Cambiar imagen"
                          : "Cambiar portada"
                        : item.key.startsWith("alojamiento.")
                          ? "Subir imagen"
                          : "Subir portada"
                  }}
                </button>
              </div>

              <div v-if="!item.key.startsWith('alojamiento.')" class="space-y-3 rounded-lg border border-white/10 bg-white/[0.03] p-3">
                <p class="text-xs font-semibold uppercase text-bochas-text-muted">
                  Información
                </p>
                <input
                  v-model="item.title"
                  type="text"
                  class="admin-input"
                  placeholder="Título"
                />
                <textarea
                  v-model="item.subtitle"
                  rows="2"
                  class="admin-input"
                  placeholder="Subtítulo"
                ></textarea>
                <textarea
                  v-if="item.key === 'home.historias'"
                  v-model="item.quote"
                  rows="3"
                  class="admin-input"
                  placeholder="Frase destacada"
                ></textarea>
                <div
                  v-if="item.key === 'home.historias'"
                  class="grid grid-cols-1 gap-2 sm:grid-cols-3"
                >
                  <input v-model="item.author" class="admin-input" placeholder="Nombre" />
                  <input v-model="item.club" class="admin-input" placeholder="Club" />
                  <input v-model="item.years" class="admin-input" placeholder="Años" />
                </div>
                <button
                  type="button"
                  class="w-full rounded-lg border border-white/10 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/15"
                  :disabled="savingKey === item.key"
                  @click="saveContent(item)"
                >
                  {{
                    savingKey === item.key
                      ? "Guardando..."
                      : "Guardar información"
                  }}
                </button>
              </div>

              <p v-if="errors[item.key]" class="text-xs text-bochas-rojo">
                {{ errors[item.key] }}
              </p>
            </div>
            </article>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import {
  actualizarSiteMedia,
  listarSiteMedia,
  subirSiteMediaImage,
} from "@/services/siteMedia/siteMediaService";

const items = ref([]);
const loading = ref(true);
const uploadingKey = ref("");
const savingKey = ref("");
const inputRefs = reactive({});
const errors = reactive({});

const homeHero = computed(
  () =>
    items.value.find((item) => item.key === "home.hero") || {
      key: "home.hero",
      label: "Home - portada principal",
    },
);

const allHomeHeroSlides = computed(() =>
  items.value
    .filter((item) => /^home\.hero\.\d+$/.test(item.key))
    .sort((a, b) => Number(a.key.split(".").pop()) - Number(b.key.split(".").pop())),
);

const homeHeroSlides = computed(() =>
  allHomeHeroSlides.value.filter(
    (item, index) =>
      index === 0 ||
      item.__visible ||
      item.imageUrl ||
      item.title ||
      item.subtitle ||
      item.alt,
  ),
);

const otherItems = computed(() =>
  items.value.filter(
    (item) =>
      item.key !== "home.hero" &&
      !/^home\.hero\.\d+$/.test(item.key) &&
      !item.key.startsWith("alojamiento."),
  ),
);

const mediaGroups = computed(() => {
  const groups = [
    {
      key: "home",
      label: "Secciones del inicio",
      description: "Imágenes que acompañan cada bloque de la página principal.",
      items: otherItems.value.filter((item) => item.key.startsWith("home.")),
    },
    {
      key: "pages",
      label: "Portadas de páginas",
      description: "Imagen principal de cada sección pública del sitio.",
      items: otherItems.value.filter((item) => !item.key.startsWith("home.")),
    },
  ];

  return groups.filter((group) => group.items.length);
});

function setInputRef(key, el) {
  if (el) inputRefs[key] = el;
}

async function loadItems() {
  loading.value = true;
  try {
    const data = await listarSiteMedia();
    items.value = data.items || [];
    if (!allHomeHeroSlides.value.length) addHomeHeroSlide();
  } finally {
    loading.value = false;
  }
}

function addHomeHeroSlide() {
  const hiddenSlide = allHomeHeroSlides.value.find(
    (item, index) =>
      index > 0 && !item.imageUrl && !item.title && !item.subtitle && !item.alt,
  );
  if (hiddenSlide) {
    hiddenSlide.__visible = true;
    return;
  }

  const numbers = allHomeHeroSlides.value.map((item) =>
    Number(item.key.split(".").pop()),
  );
  const next = numbers.length ? Math.max(...numbers) + 1 : 1;
  const label = next === 1 ? "Home - portada principal" : `Home - portada ${next}`;
  items.value.push({
    key: `home.hero.${next}`,
    label,
    imageUrl: "",
    alt: "",
    title: "",
    subtitle: "",
  });
}

function mediaPayload(item) {
  return {
    label: item.label,
    imageUrl: item.imageUrl,
    alt: item.alt,
    title: item.title,
    subtitle: item.subtitle,
    quote: item.quote,
    author: item.author,
    authorSubtitle: item.authorSubtitle,
    club: item.club,
    years: item.years,
  };
}

async function saveAlt(item) {
  return saveContent(item);
}

async function saveContent(item) {
  savingKey.value = item.key;
  try {
    errors[item.key] = "";
    await actualizarSiteMedia(item.key, mediaPayload(item));
  } catch (_) {
    errors[item.key] = "No se pudo guardar la información.";
  } finally {
    savingKey.value = "";
  }
}

async function onFileChange(item, event) {
  const file = event.target.files?.[0];
  event.target.value = "";
  if (!file) return;

  uploadingKey.value = item.key;
  errors[item.key] = "";
  try {
    const res = await subirSiteMediaImage(item.key, file);
    item.imageUrl = res.url || res.item?.imageUrl || item.imageUrl;
  } catch (_) {
    errors[item.key] = "No se pudo subir la imagen.";
  } finally {
    uploadingKey.value = "";
  }
}

onMounted(loadItems);
</script>

<style scoped>
.admin-input {
  width: 100%;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  padding: 0.6rem 0.75rem;
  font-size: 0.875rem;
  color: white;
  outline: none;
}

.admin-input:focus {
  border-color: #a52121;
}

textarea.admin-input {
  field-sizing: content;
  min-height: 5rem;
  max-height: 14rem;
  resize: vertical;
  overflow-y: auto;
  line-height: 1.5;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.28) transparent;
}

textarea.admin-input::-webkit-scrollbar {
  width: 6px;
}

textarea.admin-input::-webkit-scrollbar-track {
  background: transparent;
}

textarea.admin-input::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.24);
}

textarea.admin-input::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.4);
}

.admin-input option {
  background: #1a1f2e;
  color: #ffffff;
}

.section-heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-heading__number {
  display: inline-flex;
  height: 2rem;
  width: 2rem;
  flex: none;
  align-items: center;
  justify-content: center;
  border-radius: 0.65rem;
  background: rgba(165, 33, 33, 0.18);
  color: #ffb7b7;
  font-size: 0.75rem;
  font-weight: 700;
}
</style>
